import { ClassMode, Frequency, RequesterRole, RescheduleRequest, Session } from '../types/session';

const SESSIONS_KEY = 'scheduledSessions';
const RESCHEDULE_REQUESTS_KEY = 'rescheduleRequests';

interface TeacherSelection {
  teacher: {
    id: string;
    userId?: string;
    name: string;
    subject: string;
  };
  preference: {
    classMode: ClassMode;
    frequency: Frequency;
    preferredTimeSlot?: string;
  };
}

interface AppUser {
  id?: string;
  firstName?: string;
  lastName?: string;
  role?: 'TEACHER' | 'STUDENT' | 'ADMIN';
}

const createId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const safeParse = <T>(raw: string | null, fallback: T): T => {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

const getCurrentUser = (): AppUser | null => {
  const raw = localStorage.getItem('user');
  const parsed = safeParse<any>(raw, null);
  if (!parsed) return null;
  return parsed.user ? parsed.user : parsed;
};

const getDefaultHour = (classMode: ClassMode) => {
  if (classMode === 'OFFLINE') return 17;
  if (classMode === 'HYBRID') return 16;
  return 18;
};

const getDuration = (classMode: ClassMode) => {
  if (classMode === 'OFFLINE') return 90;
  if (classMode === 'HYBRID') return 75;
  return 60;
};

const addDays = (date: Date, days: number) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const nextWeekend = (baseDate: Date) => {
  const next = new Date(baseDate);
  const day = next.getDay();
  const daysUntilSaturday = (6 - day + 7) % 7;
  next.setDate(next.getDate() + daysUntilSaturday);
  return next;
};

const createUpcomingDates = (frequency: Frequency, classMode: ClassMode, count = 6): Date[] => {
  const now = new Date();
  const start = new Date(now);
  start.setDate(start.getDate() + 1);
  start.setHours(getDefaultHour(classMode), 0, 0, 0);

  const dates: Date[] = [];
  if (frequency === 'WEEKENDS') {
    let cursor = nextWeekend(start);
    for (let i = 0; i < count; i += 1) {
      dates.push(new Date(cursor));
      cursor = addDays(cursor, 7);
    }
    return dates;
  }

  const stepDays = frequency === 'DAILY' ? 1 : frequency === 'WEEKLY' ? 7 : 14;
  let cursor = new Date(start);
  for (let i = 0; i < count; i += 1) {
    dates.push(new Date(cursor));
    cursor = addDays(cursor, stepDays);
  }
  return dates;
};

export const getScheduledSessions = (): Session[] =>
  safeParse<Session[]>(localStorage.getItem(SESSIONS_KEY), []);

export const saveScheduledSessions = (sessions: Session[]) => {
  localStorage.setItem(SESSIONS_KEY, JSON.stringify(sessions));
};

export const getRescheduleRequests = (): RescheduleRequest[] =>
  safeParse<RescheduleRequest[]>(localStorage.getItem(RESCHEDULE_REQUESTS_KEY), []);

const saveRescheduleRequests = (requests: RescheduleRequest[]) => {
  localStorage.setItem(RESCHEDULE_REQUESTS_KEY, JSON.stringify(requests));
};

export const createSessionsFromTeacherSelection = (selection: TeacherSelection): Session[] => {
  const currentUser = getCurrentUser();
  if (!currentUser) return [];

  const studentId = currentUser.id || 'student-local';
  const studentName = `${currentUser.firstName || 'Student'} ${currentUser.lastName || ''}`.trim();
  const dates = createUpcomingDates(selection.preference.frequency, selection.preference.classMode, 6);
  const duration = getDuration(selection.preference.classMode);

  const teacherId = selection.teacher.userId || selection.teacher.id;
  const sessions = dates.map((date) => ({
    id: createId('session'),
    teacherId,
    teacherName: selection.teacher.name,
    studentId,
    studentName,
    subject: selection.teacher.subject,
    classMode: selection.preference.classMode,
    frequency: selection.preference.frequency,
    scheduledAt: date.toISOString(),
    duration,
    status: 'SCHEDULED' as const,
  }));

  const existing = getScheduledSessions();
  const next = existing.filter(
    (session) =>
      !(
        session.studentId === studentId &&
        session.teacherId === teacherId &&
        session.subject.trim().toLowerCase() === selection.teacher.subject.trim().toLowerCase()
      )
  );
  saveScheduledSessions([...next, ...sessions]);
  return sessions;
};

export const getSessionsForStudent = (studentId: string): Session[] =>
  getScheduledSessions()
    .filter((session) => session.studentId === studentId)
    .sort((a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime());

export const getSessionsForTeacher = (teacherId: string): Session[] =>
  getScheduledSessions()
    .filter((session) => session.teacherId === teacherId)
    .sort((a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime());

export const requestReschedule = (
  sessionId: string,
  requestedBy: RequesterRole,
  reason: string,
  proposedScheduledAt: string,
  fallbackSession?: Session
) => {
  const sessions = getScheduledSessions();
  const sessionFromStore = sessions.find((item) => item.id === sessionId);
  const session = sessionFromStore || fallbackSession;
  if (!session) return { ok: false, error: 'Session not found.' };
  if (!reason.trim()) return { ok: false, error: 'Reason is required.' };

  const user = getCurrentUser();
  const requestedByName = `${user?.firstName || requestedBy} ${user?.lastName || ''}`.trim();
  const requests = getRescheduleRequests();

  const request: RescheduleRequest = {
    id: createId('req'),
    sessionId,
    requestedBy,
    requestedByName,
    reason: reason.trim(),
    requestedAt: new Date().toISOString(),
    currentScheduledAt: session.scheduledAt,
    proposedScheduledAt,
    status: 'PENDING',
  };

  const baseSessions = sessionFromStore
    ? sessions
    : [...sessions.filter((item) => item.id !== session.id), session];
  const nextSessions = baseSessions.map((item) =>
    item.id === sessionId ? { ...item, status: 'RESCHEDULE_PENDING' as const } : item
  );

  saveScheduledSessions(nextSessions);
  saveRescheduleRequests([...requests, request]);
  return { ok: true, request };
};

export const reviewRescheduleRequest = (
  requestId: string,
  decision: 'APPROVED' | 'REJECTED',
  adminDecisionReason: string
) => {
  const requests = getRescheduleRequests();
  const request = requests.find((item) => item.id === requestId);
  if (!request) return { ok: false, error: 'Request not found.' };
  if (request.status !== 'PENDING') return { ok: false, error: 'Request already reviewed.' };

  const nextRequests = requests.map((item) =>
    item.id === requestId
      ? {
          ...item,
          status: decision,
          adminDecisionAt: new Date().toISOString(),
          adminDecisionReason: adminDecisionReason.trim() || undefined,
        }
      : item
  );
  saveRescheduleRequests(nextRequests);

  const sessions = getScheduledSessions();
  const nextSessions = sessions.map((session) => {
    if (session.id !== request.sessionId) return session;
    if (decision === 'APPROVED') {
      return {
        ...session,
        scheduledAt: request.proposedScheduledAt,
        status: 'SCHEDULED' as const,
      };
    }
    return { ...session, status: 'SCHEDULED' as const };
  });
  saveScheduledSessions(nextSessions);

  return { ok: true };
};

export const getCurrentUserProfile = () => getCurrentUser();
